/**
 * The gateway's wire protocol, as the deployed backend actually speaks it.
 *
 * Every frame in both directions carries a `type` discriminator, so dispatch is a switch on it
 * and nothing else. That matters: the previous client fell through to "treat it as reply text"
 * for anything it did not recognize, which turned a new frame type on a backend we do not own
 * into a spurious assistant turn.
 */

/** The STB_Client's platform. Sent as `deviceMode`; matched case-insensitively by the backend. */
export type DeviceMode = 'DISH' | 'DANY' | 'SLING';

/** The voice experience display mode. Unrelated to {@link DeviceMode} despite the reused field name. */
export type VoiceMode = 'compact' | 'fullScreen';

export interface SignInDetails {
  householdId: string;
  receiverId: string;
  zipcode: string;
  dma: string;
  /** Sling account identifier. Optional — never blocks sign-in when absent. */
  userId?: string;
  /** Sling entitlement identifier. Optional — never blocks sign-in when absent. */
  entitlementId?: string;
}

/* ── Inbound ──────────────────────────────────────────────────────────────── */

/**
 * Which turn a frame belongs to. Optional in three cases that all have to be tolerated: a frame that
 * belongs to the session rather than to a turn (the sign-in response), a gateway that has not
 * implemented the echo (the deployed Grasshopper backend sends none), and a frame the gateway raises
 * outside any turn. A frame that carries no id is therefore never treated as belonging to some
 * *other* turn — only as unattributed.
 *
 * Typed wider than what this client mints, which is a number: the echo comes back from whichever
 * backend the console is pointed at, and one that round-trips the id as a string is answering about
 * the same turn. Comparisons therefore go through `String()` rather than `===` on the raw value.
 */
interface TurnScoped {
  turnId?: string | number;
}

export interface SignInResponseFrame extends TurnScoped {
  type: 'signInResponse';
  success?: boolean;
  sessionId?: string | null;
  newSession?: boolean;
  errorMessage?: string | null;
}

/** Diagnostics. Ignorable by contract; the latency panel is built entirely out of these. */
export interface DebugFrame extends TurnScoped {
  type: 'debug';
  data?: { description?: string; content?: string | object };
  /** Server epoch ms, which is not the client's clock — see normalizeClock in session/debug.ts. */
  timestampMs: number;
}

export interface ThinkingFrame extends TurnScoped {
  type: 'thinking';
  thinkingEnabled?: boolean;
  thinkingText?: string;
  thinkingIcon?: string | null;
}

export interface ToolPayloadFrame extends TurnScoped {
  type: 'toolPayload';
  toolCallId?: string;
  toolPayloadType?: string;
  toolPayloadVersion?: number;
  toolPayload?: unknown;
}

/**
 * Raw PCM, base64'd. One frame per synthesized sentence. `chunkNumber: -1` with no `base64Audio`
 * ends the turn's speech: no more audio follows for that turn. It is sent once per turn, spoken or
 * not, and always ahead of the reply's own `-1`.
 */
export interface AudioMessageFrame extends TurnScoped {
  type: 'audioMessage';
  chunkNumber?: number;
  base64Audio?: string;
  encoding?: string;
  sampleRate?: number;
  bitsPerSample?: number;
  channels?: number;
}

/** A reply chunk. `chunkNumber: -1` terminates the reply and carries no text. */
export interface ModelResponseChunkFrame extends TurnScoped {
  type: 'modelResponseChunk';
  chunkNumber: number;
  chunk?: string;
}

/** The whole utterance recognized so far. Replaces its predecessor rather than appending. */
export interface TranscriptionFrame extends TurnScoped {
  type: 'transcription';
  text?: string;
}

export interface ErrorFrame extends TurnScoped {
  type: 'error';
  errorMessage?: string;
}

/**
 * Declared by the server's message set but not acted on here: what the receiver is showing is
 * the receiver's business, and the socket closing is already observable. Named so the frame log
 * shows them as known rather than unrecognized.
 */
export interface ScreenStateInboundFrame extends TurnScoped {
  type: 'screenState';
  [key: string]: unknown;
}

export interface CloseSessionFrame extends TurnScoped {
  type: 'closeSession';
  [key: string]: unknown;
}

/**
 * Instructs the client to change its voice experience state. `state` is typed as a raw string,
 * not {@link VoiceMode}: the contract allows future values this client does not know yet, and
 * says clients SHOULD ignore ones they do not recognize rather than treat them as a protocol
 * error. Narrowing to `VoiceMode` happens where the value is consumed.
 */
export interface ChangeVoiceStateFrame extends TurnScoped {
  type: 'changeVoiceState';
  state?: string;
}

export type InboundFrame =
  | SignInResponseFrame
  | DebugFrame
  | ThinkingFrame
  | ToolPayloadFrame
  | AudioMessageFrame
  | ModelResponseChunkFrame
  | TranscriptionFrame
  | ErrorFrame
  | ScreenStateInboundFrame
  | CloseSessionFrame
  | ChangeVoiceStateFrame;

/** The values {@link ChangeVoiceStateFrame.state} may carry that this client acts on. */
const VOICE_STATES: readonly VoiceMode[] = ['compact', 'fullScreen'];

/** Narrows a wire value to a {@link VoiceMode}, or `null` for one this client does not recognize. */
export function asVoiceMode(state: string | undefined): VoiceMode | null {
  return (VOICE_STATES as readonly string[]).includes(state ?? '') ? (state as VoiceMode) : null;
}

/* ── Outbound ─────────────────────────────────────────────────────────────── */

/**
 * Which LLM answers this session. Both halves are required together: the backend refuses a model id
 * without the provider whose id it is, having no way to tell whose it is. A provider the target
 * deployment has disabled is refused at sign-in, with the reason in `signInResponse.errorMessage`.
 */
export interface SignInLlmMetadata {
  provider: string;
  model: string;
}

/**
 * Which TTS voice this session speaks with. The provider and voice are named together in the
 * handshake, with an optional model id for the providers that have one; the backend refuses a voice
 * whose provider was not named, or a provider the target deployment has disabled, with the reason in
 * `signInResponse.errorMessage`.
 */
export interface SignInTtsMetadata {
  provider: string;
  voice: string;
  /** Omitted for providers with no model concept (Polly). */
  model?: string;
}

/**
 * The providers this session asks for, one block per capability. Each block — and the whole object —
 * is omitted to name nothing for it, leaving the deployment on its own default.
 */
export interface SignInMetadata {
  llm?: SignInLlmMetadata;
  tts?: SignInTtsMetadata;
}

export interface SignInRequestFrame {
  type: 'signInRequest';
  /** The platform, e.g. `DISH`/`DANY`/`SLING`. Required and validated by the backend. */
  deviceMode: DeviceMode;
  /** The display mode: `compact` or `fullScreen`. Required and validated by the backend. */
  voiceMode: VoiceMode;
  signInDetails: SignInDetails & { hostReceiverId: string };
  /** Omitted when it would name neither a model nor a voice. */
  metadata?: SignInMetadata;
}

export interface ChatRequestFrame {
  type: 'chatRequest';
  /**
   * Which user interaction this request belongs to: the turn's ordinal in the session, counted from
   * zero here and echoed by the gateway on everything it sends back in answer — which is what lets a
   * log line or a frame be attributed to a turn rather than only to the session.
   */
  turnId: number;
  message: string;
}

/** One frame of microphone PCM. `chunkNumber: -1` with empty audio ends the utterance. */
export interface AudioInputFrame {
  type: 'audioInput';
  /** The interaction this utterance belongs to; the same ordinal on every frame of it. */
  turnId: number;
  chunkNumber: number;
  base64Audio: string;
  encoding?: string;
  sampleRate?: number;
  bitsPerSample?: number;
  channels?: number;
}

/**
 * What the client is currently showing, so Andromeda can resolve on-screen references. This
 * client has no real notion of "current screen" — it is a single voice console, not a multi-screen
 * STB — so `screen`/`description` are fixed placeholders; the field that matters here is
 * `voiceMode`, sent to keep the server in sync when the voice experience state changes mid-session.
 */
export interface ScreenStateFrame {
  type: 'screenState';
  voiceMode: VoiceMode;
  screen: string;
  description: unknown;
}

export type OutboundFrame =
  SignInRequestFrame | ChatRequestFrame | AudioInputFrame | ScreenStateFrame;

/** Frame types this client acts on, as opposed to merely logging. */
const HANDLED: ReadonlySet<string> = new Set([
  'signInResponse',
  'debug',
  'thinking',
  'toolPayload',
  'audioMessage',
  'modelResponseChunk',
  'transcription',
  'error',
  'screenState',
  'closeSession',
  'changeVoiceState',
]);

export function isKnownFrameType(type: unknown): boolean {
  return typeof type === 'string' && HANDLED.has(type);
}

/** Parses an inbound message, returning null for anything that is not a typed JSON frame. */
export function parseFrame(raw: string): InboundFrame | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const type = (value as { type?: unknown }).type;
  return typeof type === 'string' ? (value as InboundFrame) : null;
}

/** Audio is megabytes of base64; the frame log keeps the shape and drops the payload. */
export function summarizeFrame(frame: object): string {
  const shallow: Record<string, unknown> = { ...(frame as Record<string, unknown>) };
  if (typeof shallow.base64Audio === 'string') {
    shallow.base64Audio = `‹${shallow.base64Audio.length} base64 chars›`;
  }
  return JSON.stringify(shallow, null, 2);
}
